<template>
  <div class="modal-overlay" @click.self="$emit('cancel')">
    <div class="modal-card evento-modal">
      <h2 class="modal-title">{{ isEdit ? `Editar: ${evento.nome}` : 'Novo evento' }}</h2>

      <div class="tabs">
        <button v-for="t in tabs" :key="t.key" class="tab"
                :class="{ active: tab === t.key }" @click="tab = t.key">{{ t.label }}</button>
      </div>

      <form @submit.prevent="onSave">
        <section v-show="tab === 'dados'" class="section">
          <div class="form-row">
            <label class="label">Nome do evento *</label>
            <input class="input" v-model="form.nome" required maxlength="200" />
          </div>
          <div class="form-row">
            <label class="label">Slug (URL) *</label>
            <input class="input" v-model="form.slug" required placeholder="encontro-2026"
                   @input="form.slug = form.slug.toLowerCase().replace(/[^a-z0-9-]/g, '')" />
            <span class="help-text">A pagina ficara em <code>/{{ form.slug || '...' }}</code></span>
          </div>
          <div class="form-grid">
            <div class="form-row">
              <label class="label">Data inicio</label>
              <input class="input" type="date" v-model="form.dataInicio" />
            </div>
            <div class="form-row">
              <label class="label">Data fim</label>
              <input class="input" type="date" v-model="form.dataFim" />
            </div>
          </div>
          <div class="form-row">
            <label class="label">Status</label>
            <select class="select" v-model="form.status">
              <option value="RASCUNHO">Rascunho — em preparacao, ninguem ve</option>
              <option value="ABERTO">Aberto — aceita inscricoes publicamente</option>
              <option value="ENCERRADO">Encerrado — pagina ainda visivel mas nao aceita mais</option>
              <option value="ARQUIVADO">Arquivado — desativa de vez</option>
            </select>
            <span class="help-text">{{ statusHelp }}</span>
          </div>
          <div class="form-row">
            <label class="label">Descricao</label>
            <textarea class="textarea" v-model="form.descricao" rows="4" maxlength="5000"></textarea>
          </div>
          <div class="form-row">
            <label class="label">Mensagem de boas-vindas</label>
            <textarea class="textarea" v-model="form.mensagemBoasVindas" rows="2" maxlength="2000"></textarea>
          </div>

          <div class="form-row">
            <label class="label">Imagem de capa (banner)</label>
            <span class="help-text">
              Aparece quando alguém compartilhar o link do evento no WhatsApp, Facebook, etc.
              Recomendado: 1200x630px, até 8MB (JPG, PNG ou WEBP).
            </span>

            <div class="banner-upload">
              <button type="button" class="btn-secondary" :disabled="uploadingBanner" @click="abrirSeletorBanner">
                {{ uploadingBanner ? 'Enviando...' : (form.banner ? 'Trocar imagem' : 'Escolher imagem') }}
              </button>
              <button v-if="form.banner" type="button" class="btn-link" :disabled="uploadingBanner" @click="removerBanner">
                Remover
              </button>
              <input ref="bannerInput" type="file" accept="image/*" hidden @change="onBannerSelecionado" />
            </div>

            <p v-if="uploadErroBanner" class="banner-erro">{{ uploadErroBanner }}</p>

            <div v-if="form.banner" class="banner-preview">
              <img :src="form.banner" alt="Preview da capa" @error="bannerErro = true" @load="bannerErro = false" />
              <p v-if="bannerErro" class="banner-erro">Não consegui carregar essa imagem.</p>
            </div>
          </div>

          <div class="form-row">
            <label class="check-row">
              <input type="checkbox" v-model="form.temCheckin" />
              <span><strong>Habilitar check-in</strong> nesse evento</span>
            </label>
            <span class="help-text">
              Se ligado, cada inscrito com pagamento quitado recebe um código de check-in (QR Code) acessível em /inscricao/&lt;id&gt;/qr.
              Voluntários vinculados podem confirmar presença em /scan.
            </span>
          </div>

          <div class="form-row">
            <label class="label">Limite de inscritos (vagas)</label>
            <input
              class="input"
              type="number"
              min="1"
              placeholder="Deixe em branco para ilimitado"
              v-model.number="form.limiteInscritos"
            />
            <span class="help-text">
              Quando o limite for atingido, a página do evento mostra “Vagas esgotadas” e novas inscrições são bloqueadas. Inscrições canceladas liberam vaga.
            </span>
          </div>
        </section>

        <section v-show="tab === 'campos'" class="section">
          <p class="section-help">Adicione campos extras que voce quer perguntar a cada inscrito (alem do nome, CPF, email, telefone).</p>
          <CamposCustomEditor v-model="form.campos" />
        </section>

        <section v-show="tab === 'planos'" class="section">
          <p v-if="!isEdit" class="section-help">Salve o evento primeiro pra cadastrar planos.</p>
          <PlanosManager v-else :eventoId="evento.id" />
        </section>

        <section v-show="tab === 'cupons'" class="section">
          <p v-if="!isEdit" class="section-help">Salve o evento primeiro pra cadastrar cupons.</p>
          <CuponsManager v-else :eventoId="evento.id" />
        </section>

        <section v-if="authStore.canSeeFinanceiroCompleto" v-show="tab === 'financeiro'" class="section">
          <p class="section-help">
            A comissao e somada POR CIMA do valor do plano, junto com a taxa do Asaas. O evento recebe o valor do plano limpo.
          </p>

          <div class="form-grid">
            <div class="form-row">
              <label class="label">Tipo de comissao</label>
              <select class="select" v-model="form.comissaoTipo">
                <option value="FIXO">Fixo (R$ por inscricao)</option>
                <option value="PERCENTUAL">Percentual (% do valor pago)</option>
              </select>
            </div>
            <div class="form-row">
              <label class="label">
                {{ form.comissaoTipo === 'PERCENTUAL' ? 'Comissao (%)' : 'Comissao (R$)' }}
              </label>
              <input class="input" type="number" min="0" :step="form.comissaoTipo === 'PERCENTUAL' ? '0.1' : '0.01'"
                     v-model.number="form.comissaoValor" />
              <span class="help-text">{{ comissaoHelp }}</span>
            </div>
          </div>

          <div class="form-grid">
            <div class="form-row">
              <label class="label">Responsavel do evento</label>
              <input class="input" v-model="form.responsavelNome" placeholder="Nome de quem coordena" maxlength="120" />
            </div>
            <div class="form-row">
              <label class="label">Contato/PIX do responsavel</label>
              <input class="input" v-model="form.responsavelContato" placeholder="Chave PIX, WhatsApp, etc" maxlength="120" />
            </div>
          </div>

          <div class="form-row">
            <label class="label">Organizador (admin com login restrito)</label>
            <select class="select" v-model="form.organizadorAdminId">
              <option :value="null">Nenhum</option>
              <option v-for="o in organizadores" :key="o.id" :value="o.id" :disabled="o.vinculado">
                {{ o.name }} ({{ o.email }}){{ o.vinculado ? ' — ja em outro evento' : '' }}
              </option>
            </select>
            <span class="help-text">
              Cada organizador so pode coordenar 1 evento ativo. Pra liberar, arquive o evento anterior.
              Crie um usuario com papel "Organizador" em /admin/usuarios.
            </span>
          </div>
        </section>

        <div v-if="error" class="error-banner">{{ error }}</div>

        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="$emit('cancel')">Cancelar</button>
          <button type="submit" class="btn-primary" :disabled="saving">{{ saving ? 'Salvando...' : 'Salvar' }}</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { calcularBreakdownGrupo } from '../../lib/breakdownGrupo'

// Valor de plano usado so pra ilustrar o efeito da comissao no formulario.
const EXEMPLO_PLANO = 20

import CamposCustomEditor from './CamposCustomEditor.vue'
import PlanosManager from './PlanosManager.vue'
import CuponsManager from './CuponsManager.vue'
import { useAuthStore } from '../../stores/auth'

function emptyForm() {
  return {
    slug: '', nome: '', descricao: '', mensagemBoasVindas: '',
    banner: '', dataInicio: '', dataFim: '', status: 'RASCUNHO', campos: [],
    comissaoTipo: 'FIXO', comissaoValor: 5,
    responsavelNome: '', responsavelContato: '',
    organizadorAdminId: null,
    temCheckin: false,
    limiteInscritos: null
  }
}

function toYmd(v) {
  if (!v) return ''
  try { return new Date(v).toISOString().slice(0, 10) } catch { return '' }
}

export default {
  name: 'EventoForm',
  components: { CamposCustomEditor, PlanosManager, CuponsManager },
  props: { evento: { type: Object, default: null } },
  emits: ['save', 'cancel'],
  data() {
    return {
      tab: 'dados',
      organizadores: [],
      form: emptyForm(),
      saving: false, error: '',
      bannerErro: false,
      uploadingBanner: false,
      uploadErroBanner: ''
    }
  },
  computed: {
    isEdit() { return !!this.evento?.id },
    authStore() { return useAuthStore() },
    tabs() {
      const base = [
        { key: 'dados', label: 'Dados' },
        { key: 'campos', label: 'Campos custom' },
        { key: 'planos', label: 'Planos' },
        { key: 'cupons', label: 'Cupons' }
      ]
      if (this.authStore.canSeeFinanceiroCompleto) {
        base.push({ key: 'financeiro', label: 'Comissão & repasse' })
      }
      return base
    },
    statusHelp() {
      const map = {
        RASCUNHO: 'A pagina publica /' + (this.form.slug || '<slug>') + ' vai mostrar "inscricoes nao disponiveis".',
        ABERTO: 'Pessoas vao conseguir se inscrever em /' + (this.form.slug || '<slug>') + ' agora.',
        ENCERRADO: 'A pagina existe mas mostra que ja encerrou.',
        ARQUIVADO: 'Some da pagina publica. Permanece nos relatorios internos.'
      }
      return map[this.form.status] || ''
    },
    comissaoHelp() {
      const v = Number(this.form.comissaoValor) || 0
      const cfg = v > 0 ? { tipo: this.form.comissaoTipo, valor: v } : null
      const pix = calcularBreakdownGrupo(EXEMPLO_PLANO, 'PIX', 1, cfg)
      const cc = calcularBreakdownGrupo(EXEMPLO_PLANO, 'CREDIT_CARD', 1, cfg)
      const m = n => `R$ ${Number(n).toFixed(2).replace('.', ',')}`
      const padrao = v > 0 ? '' : ' Deixando 0, usa o padrao de R$ 5,00.'
      return `Num plano de ${m(EXEMPLO_PLANO)}: o inscrito paga ${m(pix.total)} no PIX ou ${m(cc.total)} no cartao. `
        + `O evento recebe ${m(EXEMPLO_PLANO)}, a plataforma ${m(pix.comissaoPlataforma)} e o Asaas ${m(pix.taxaGateway)}.${padrao}`
    }
  },
  async mounted() {
    if (this.evento) {
      this.form = {
        slug: this.evento.slug,
        nome: this.evento.nome,
        descricao: this.evento.descricao || '',
        mensagemBoasVindas: this.evento.mensagemBoasVindas || '',
        banner: this.evento.banner || '',
        dataInicio: toYmd(this.evento.dataInicio),
        dataFim: toYmd(this.evento.dataFim),
        status: this.evento.status,
        campos: Array.isArray(this.evento.campos) ? [...this.evento.campos] : [],
        comissaoTipo: this.evento.comissaoTipo || 'FIXO',
        comissaoValor: this.evento.comissaoValor || 0,
        responsavelNome: this.evento.responsavelNome || '',
        responsavelContato: this.evento.responsavelContato || '',
        organizadorAdminId: this.evento.organizadorAdminId || null,
        temCheckin: this.evento.temCheckin === true,
        limiteInscritos: this.evento.limiteInscritos ?? null
      }
    }
    try {
      const api = (await import('../../lib/api')).default
      const [adminsRes, eventosRes] = await Promise.all([
        api.get('/admins'),
        api.get('/eventos')
      ])
      const todos = (adminsRes.data.admins || []).filter(a => a.role === 'ORGANIZADOR')
      const vinculadoIds = new Set(
        (eventosRes.data.eventos || [])
          .filter(e => e.status !== 'ARQUIVADO' && e.organizadorAdminId)
          .filter(e => !this.evento || e.id !== this.evento.id)
          .map(e => e.organizadorAdminId)
      )
      this.organizadores = todos.map(o => ({ ...o, vinculado: vinculadoIds.has(o.id) }))
    } catch {}
  },
  methods: {
    abrirSeletorBanner() {
      this.uploadErroBanner = ''
      this.$refs.bannerInput?.click()
    },
    async onBannerSelecionado(ev) {
      const file = ev.target.files?.[0]
      ev.target.value = ''
      if (!file) return
      if (file.size > 8 * 1024 * 1024) {
        this.uploadErroBanner = 'Imagem muito grande. Máximo 8MB.'
        return
      }
      this.uploadingBanner = true
      this.uploadErroBanner = ''
      try {
        const api = (await import('../../lib/api')).default
        const fd = new FormData()
        fd.append('banner', file)
        if (this.evento?.slug) fd.append('tag', this.evento.slug)
        const { data } = await api.post('/uploads/banner', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        this.form.banner = data.url
        this.bannerErro = false
      } catch (err) {
        this.uploadErroBanner = err.response?.data?.message || 'Falha ao enviar a imagem.'
      } finally {
        this.uploadingBanner = false
      }
    },
    removerBanner() {
      this.form.banner = ''
      this.uploadErroBanner = ''
      this.bannerErro = false
    },
    async onSave() {
      this.error = ''
      this.saving = true
      try {
        const payload = {
          ...this.form,
          banner: this.form.banner || null,
          dataInicio: this.form.dataInicio || null,
          dataFim: this.form.dataFim || null,
          descricao: this.form.descricao || null,
          mensagemBoasVindas: this.form.mensagemBoasVindas || null,
          limiteInscritos: Number.isFinite(Number(this.form.limiteInscritos)) && Number(this.form.limiteInscritos) > 0
            ? Number(this.form.limiteInscritos)
            : null
        }
        this.$emit('save', payload)
      } catch (err) {
        this.error = err.response?.data?.message || 'Falha ao salvar'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.evento-modal { max-width: 720px; }
.tabs {
  display: flex; gap: var(--spacing-xs);
  margin-bottom: var(--spacing-base);
  border-bottom: 1px solid var(--color-bg-separator);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}
.tabs::-webkit-scrollbar { height: 4px; }
.tabs::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 2px; }
.tab {
  background: none; border: none; cursor: pointer;
  padding: var(--spacing-sm) var(--spacing-base);
  font-size: var(--text-sm); font-weight: 600;
  color: var(--color-text-muted);
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  white-space: nowrap;
  flex-shrink: 0;
  min-height: 40px;
}
.tab.active {
  color: var(--color-accent);
  border-bottom-color: var(--color-accent);
}
.section { display: flex; flex-direction: column; gap: var(--spacing-md); }
.section-help { color: var(--color-text-muted); font-size: var(--text-sm); }
.form-row { display: flex; flex-direction: column; gap: 4px; }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-md); }
.check-row { display: inline-flex; align-items: center; gap: 8px; cursor: pointer; }
.check-row input { accent-color: var(--color-accent); }
.error-banner {
  background: #fee2e2; color: #b91c1c;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-md);
  margin-top: var(--spacing-md);
  font-size: var(--text-sm);
}
code { background: var(--color-bg-hover); padding: 1px 4px; border-radius: 3px; font-size: 11px; }
.banner-upload {
  display: flex; align-items: center; gap: var(--spacing-sm);
  margin-top: 4px;
}
.btn-link {
  background: none; border: none;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
}
.btn-link:hover { color: var(--color-text); }
.btn-link:disabled { opacity: 0.5; cursor: not-allowed; }
.banner-preview {
  margin-top: 8px;
  border-radius: var(--radius-md);
  overflow: hidden;
  border: 1px solid var(--color-border);
  background: var(--color-bg-hover);
}
.banner-preview img {
  width: 100%;
  max-height: 240px;
  object-fit: cover;
  display: block;
}
.banner-erro {
  padding: var(--spacing-sm);
  margin: 0;
  color: #b91c1c;
  font-size: var(--text-sm);
}
@media (max-width: 540px) {
  .form-grid { grid-template-columns: 1fr; }
}
</style>
